import { Navigate } from "@solidjs/router";
import { createEffect, createResource, Show, type JSX } from "solid-js";
import { api } from "@/shared/api/client";
import { AppShell } from "@/shared/components/AppShell";
import { currentUser, setCurrentUser, token } from "./session";

/**
 * Layout for every signed-in route.
 *
 * A token alone is not enough to render the shell: the sidebar, the app
 * switcher, and the landing redirect all depend on what this user may see, so
 * the guard waits for /api/auth/me before showing anything. Because `token` is
 * a signal, a 401 handled in the API client clears it and this redirects on the
 * spot, without any component needing to check.
 */
export default function RequireAuth(props: { children?: JSX.Element }) {
  const [me] = createResource(
    () => token() || null,
    async () => {
      // Read the result rather than unwrapping it: a rejected fetch would put
      // the resource in an error state that rethrows on every later read, and
      // the API client has already dropped the dead token by this point.
      const { data } = await api.GET("/api/auth/me", {});
      return data ?? null;
    },
  );

  createEffect(() => {
    const user = me();
    if (user) setCurrentUser(user);
  });

  return (
    <Show when={token()} fallback={<Navigate href="/login" />}>
      <Show
        when={currentUser()}
        fallback={<div class="view active" style={{ padding: "24px" }} />}
      >
        <AppShell>{props.children}</AppShell>
      </Show>
    </Show>
  );
}
